import { HttpError } from "../types/Error";
import { Game } from "../types/Igdb";
import { MetaStorePageRequest, MetaTitle } from "../types/MetaStore";
import { PagedResults } from "../types/PagedResults";
import { NewTitle, Title, TitleSummary } from "../types/Title";
import {
  StartTitleImportRequest,
  TitleAuditFindings,
  TitleImportRun,
  TitleImportStatus,
} from "../types/TitleImport";
import { TitleCategories } from "../types/TitleCategories";
import useApiClient from "./ApiClient";

/** Thrown when an import is refused because one is already queued or running. */
export class TitleImportInProgressError extends Error {
  constructor() {
    super("A title import is already queued or running.");
    this.name = "TitleImportInProgressError";
  }
}

const useTitlesService = () => {
    const { get, post, put, delete: del } = useApiClient();

    const getCategories = async (): Promise<TitleCategories> => {
        try {
            const item = await get<TitleCategories>(`/titles/categories`);
            // Check if the response is an error.
            if ((item as HttpError).errorCode) {
                throw (item as HttpError).description;
            }
            return item as TitleCategories;
        } catch (error) {
            console.error("Error fetching title categories:", error);
            throw error;
        }
    }

    const getTitle = async (titleId: string): Promise<Title> => {
        try {
            const item = await get<Title>(`/titles/${titleId}`);
            // Check if the response is an error.
            if ((item as HttpError).errorCode) {
                throw (item as HttpError).description;
            }
            return item as Title;
        } catch (error) {
            console.error("Error fetching title:", error);
            throw error;
        }
    }

    const searchTitles = async (
        searchTerm?: string,
        page?: number,
        orderBy?: string,
        orderDirection?: string
      ): Promise<PagedResults<TitleSummary>> => {
        try {
          const postData = {
            searchTerm,
            page: page ?? 1,
            itemsPerPage: 10,
            orderBy: orderBy ?? "name",
            orderDirection: orderDirection ?? "asc",
          };
    
          const result = await post<PagedResults<TitleSummary>>(
            `/titles/search`,
            postData
          );
    
          // Check if the response is an error.
          if ((result as HttpError).errorCode) {
            throw (result as HttpError).description;
          }
    
          return result as PagedResults<TitleSummary>;
        } catch (error) {
          console.error("Error searching titles:", error);
          throw error;
        }
      };

    const createTitle = async (title: NewTitle): Promise<Title> => {
        try {
            const result = await post<Title>(`/admin/titles`, title);
            // Check if the response is an error.
            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }
            return result as Title;
        } catch (error) {
            console.error("Error creating title:", error);
            throw error;
        }
    }

    const updateTitle = async (titleId: string, title: Title): Promise<Title> => {
        try {
            const result = await put<Title>(`/admin/titles/${titleId}`, title);
            // Check if the response is an error.
            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }
            return result as Title;
        } catch (error) {
            console.error("Error updating title:", error);
            throw error;
        }
    }

    const deleteTitle = async (titleId: string): Promise<void> => {
        try {
            const result = await del(`/admin/titles/${titleId}`);
            // Check if the response is an error.
            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }
        }
        catch (error) {
            console.error("Error deleting title:", error);
            throw error;
        }
    }

    const getIgdbGame = async (igdbId: string): Promise<Game> => {
        try {
            const item = await get<Game>(`/admin/titles/igdb/${igdbId}`);
            // Check if the response is an error.
            if ((item as HttpError).errorCode) {
                throw (item as HttpError).description;
            }
            return item as Game;
        }
        catch (error) {
            console.error("Error fetching IGDB game:", error);
            throw error;
        }
    }

    const getMetaStoreTitle = async (url: string): Promise<MetaTitle> => {
        try {

            const request: MetaStorePageRequest = {
                url: url
            };

            const item = await post<MetaTitle>(`/admin/titles/metastore/`, request);
            // Check if the response is an error.
            if ((item as HttpError).errorCode) {
                throw (item as HttpError).description;
            }
            return item as MetaTitle;
        }
        catch (error) {
            console.error("Error fetching MetaStore title:", error);
            throw error;
        }
    }

    const startTitleImport = async (fromDate: string, toDate: string): Promise<TitleImportRun> => {
        try {
            const request: StartTitleImportRequest = { fromDate, toDate };

            const result = await post<TitleImportRun>(`/admin/titles/import`, request);

            const error = result as HttpError;

            if (error.errorCode) {
                // Only one import may run at a time, so a conflict is an expected answer rather
                // than a fault. The caller shows the run that is already in flight.
                if (error.errorCode === 409) {
                    throw new TitleImportInProgressError();
                }

                throw error.description;
            }

            return result as TitleImportRun;
        } catch (error) {
            console.error("Error starting title import:", error);
            throw error;
        }
    }

    const getTitleImportStatus = async (): Promise<TitleImportStatus> => {
        try {
            const result = await get<TitleImportStatus>(`/admin/titles/import`);

            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }

            return result as TitleImportStatus;
        } catch (error) {
            console.error("Error fetching title import status:", error);
            throw error;
        }
    }

    const cancelTitleImport = async (runId: string): Promise<TitleImportRun> => {
        try {
            const result = await post<TitleImportRun>(`/admin/titles/import/${runId}/cancel`, {});

            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }

            return result as TitleImportRun;
        } catch (error) {
            console.error("Error cancelling title import:", error);
            throw error;
        }
    }

    const refreshTitleFromIgdb = async (titleId: string): Promise<Title> => {
        try {
            const result = await post<Title>(`/admin/titles/${titleId}/refresh`, {});

            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }

            return result as Title;
        } catch (error) {
            console.error("Error refreshing title from IGDB:", error);
            throw error;
        }
    }

    /** Queues an audit pass over the next chunk of the catalogue. */
    const startTitleAudit = async (restart: boolean = false): Promise<TitleImportRun> => {
        try {
            const result = await post<TitleImportRun>(
                `/admin/titles/audit?restart=${restart}`, {});

            const error = result as HttpError;

            if (error.errorCode) {
                // The audit shares the single run slot with imports, so a conflict is expected
                // rather than a fault.
                if (error.errorCode === 409) {
                    throw new TitleImportInProgressError();
                }

                throw error.description;
            }

            return result as TitleImportRun;
        } catch (error) {
            console.error("Error starting title audit:", error);
            throw error;
        }
    }

    /** Queues removal of exactly the findings given. Anything left out stays flagged. */
    const applyTitleAudit = async (findingIds: string[]): Promise<TitleImportRun> => {
        try {
            const result = await post<TitleImportRun>(`/admin/titles/audit/apply`, { findingIds });

            const error = result as HttpError;

            if (error.errorCode) {
                if (error.errorCode === 409) {
                    throw new TitleImportInProgressError();
                }

                throw error.description;
            }

            return result as TitleImportRun;
        } catch (error) {
            console.error("Error applying title audit:", error);
            throw error;
        }
    }

    const getTitleAuditFindings = async (): Promise<TitleAuditFindings> => {
        try {
            const result = await get<TitleAuditFindings>(`/admin/titles/audit/findings`);

            if ((result as unknown as HttpError).errorCode) {
                throw (result as unknown as HttpError).description;
            }

            return result as TitleAuditFindings;
        } catch (error) {
            console.error("Error fetching title audit findings:", error);
            throw error;
        }
    }

    return {
        getTitle,
        searchTitles,
        createTitle,
        updateTitle,
        deleteTitle,
        getIgdbGame,
        getMetaStoreTitle,
        getCategories,
        startTitleImport,
        getTitleImportStatus,
        cancelTitleImport,
        startTitleAudit,
        applyTitleAudit,
        getTitleAuditFindings,
        refreshTitleFromIgdb
    }
}

export default useTitlesService;